import * as betServiceApi from "@/api/bet-service-api";
import * as bigeneralApi from "@/api/bigeneral-api";
import * as contentServiceApi from "@/api/content-service-api";
import * as orchestrationsApi from "@/api/orchestrations-api";
import * as inagamiApi from "@/api/inagami-api";
import * as v1Api from "@/api/v1-api";
import * as sportsServiceApi from "@/api/sports-service-api";
import type { DrilldownNode } from "@/shared/model/drilldown-node";
import type { Event, EventDrilldown, EventDrilldownDetail } from "@/shared/model/event";
import type { SazkaEventsDetailParams } from "@/api/interfaces/sazka-events-detail";
import type { SazkaEventsListParams } from "@/api/interfaces/sazka-events-list";
import type { SazkaTrendingEventsListParams } from "@/api/interfaces/sazka-trending-events-list";
import type { SazkaEventsDrilldownListParams } from "@/api/interfaces/sazka-events-drilldown-list";
import type { SazkaEventsDrilldownDetailParams } from "@/api/interfaces/sazka-events-drilldown-detail";
import type { Search } from "@/shared/model/search";
import type { DetailedSearch } from "@/shared/model/detailed-search";
import type { SazkaSearchParams } from "@/api/interfaces/sazka-search";
import type { SazkaDetailedSearchParams } from "../interfaces/sazka-detailed-search";
import type { SazkaSportsParams } from "@/api/interfaces/sazka-sports";
import type { BetParams, BetCashoutParams, BetsParams, BetResponse } from "@/api/interfaces/bet";
import type { SlipBuildParams, SlipBuildData, SlipBuild } from "@/api/interfaces/slip-build";
import type { BIGeneralBetratio } from "@/shared/model/bigeneral";
import type { OutrightsParams } from "@/api/interfaces/content-service";
import { CmsContent } from "@/shared/model/cms-content";
import { CmsContentParams } from "@/api/interfaces/cms-content";
import { EventDetail } from "@/shared/model/event";
import { GetPregenerated } from "@/shared/model/inagami";
import { BetData, BetsDataWithPaginationToken } from "@/shared/model/bet";
import { RemoveOthers, RequestOptions } from "@/api/interfaces/common";
import { StateEnum, useApiStore } from "@/api/stores/use-api-store";
import { SlipBet, SlipBetData } from "@/api/interfaces/slip-bet";
import { CashoutSubscription } from "@/api/interfaces/cashout-subscription";
import { EventAccessStream } from "@/api/interfaces/event-access-stream";
import { PrematchLeaguesResponse } from "@/api/interfaces/v1";

// Websocket can be turned off here... For debugging purposes only!
const WEBSOCKET_ENABLED = import.meta.env.VITE_DISABLE_WEBSOCKET !== "true";

const storeEvents = async <T extends Event>(
    apiStore,
    stateName: StateEnum,
    events: T[] | undefined,
    wsDataEnabled: boolean | undefined,
    removeOthers: RemoveOthers | boolean | undefined,
): Promise<T[] | undefined> =>
    wsDataEnabled ? (await apiStore?.storeEvents(stateName, events, removeOthers)) || events : events;

const storeBets = async (
    apiStore,
    bets: BetData[] | undefined,
    wsDataEnabled: boolean | undefined,
): Promise<BetData[] | undefined> => (wsDataEnabled ? (await apiStore?.storeBets(bets)) || bets : bets);

export default function useOpenbetApi() {
    // We can conditionally turn of websocket here
    const apiStore = WEBSOCKET_ENABLED ? useApiStore() : undefined;

    // content-service-api
    const getDrilldownNodes = async (): Promise<DrilldownNode[]> => {
        const response = await contentServiceApi.getSazkaDrilldownNodes();
        return response?.data.drilldownNodes;
    };

    const getEvents = async (params: SazkaEventsListParams, options?: RequestOptions): Promise<Event[]> => {
        const response = await contentServiceApi.getSazkaEventsList({ ...params });
        const retVal = await storeEvents(
            apiStore,
            StateEnum.Event,
            response?.data?.events,
            options?.wsData?.update,
            options?.wsData?.removeOthers,
        );
        return retVal;
    };

    const getEventsDetail = async (
        params: SazkaEventsDetailParams,
        options?: RequestOptions,
    ): Promise<EventDetail[]> => {
        const response = await contentServiceApi.getSazkaEventsDetail({ ...params });
        const retVal = await storeEvents(
            apiStore,
            StateEnum.EventDetail,
            response?.data?.events,
            options?.wsData?.update,
            options?.wsData?.removeOthers,
        );
        return retVal;
    };

    const getTrendingEvents = async (params: SazkaTrendingEventsListParams): Promise<Event[]> => {
        const response = await contentServiceApi.getSazkaTrendingEventsList(params);
        return response?.data.events;
    };

    const getSearch = async (params: SazkaSearchParams): Promise<Search> => {
        const response = await contentServiceApi.getSazkaSearch(params);
        return response?.search;
    };

    const getSports = async (params?: SazkaSportsParams): Promise<DrilldownNode[]> => {
        const response = await contentServiceApi.getSazkaSports(params);
        return response?.data.drilldownNodes[0]?.drilldownNodes;
    };

    const getCmsContent = async (params: CmsContentParams): Promise<CmsContent> => {
        const response = await contentServiceApi.getCmsContent(params);
        return response?.data.cmsContent;
    };

    const getSazkaLeagues = async (params: SazkaSportsParams): Promise<DrilldownNode[]> => {
        const response = await contentServiceApi.getSazkaLeagues(params);
        return response?.data.drilldownNodes?.[0]?.drilldownNodes;
    };

    const getOutrightsEvents = async (params: OutrightsParams): Promise<Event[]> => {
        const response = await contentServiceApi.getOutrightsList(params);
        return response?.data?.events;
    };

    // orchestrations
    const getEventsDrilldown = async (
        params: SazkaEventsDrilldownListParams,
        options?: RequestOptions,
    ): Promise<EventDrilldown[]> => {
        const response = await orchestrationsApi.getSazkaEventsDrilldownList(params);
        const retVal = await storeEvents(
            apiStore,
            StateEnum.EventDrilldown,
            response?.data?.events,
            options?.wsData?.update,
            options?.wsData?.removeOthers,
        );
        return retVal;
    };

    const getTrendingEventsDrilldown = async (): Promise<EventDrilldown[]> => {
        const response = await orchestrationsApi.getSazkaTrendingEventsDrilldownList();
        return response?.data?.events;
    };

    const getEventsDrilldownDetail = async (
        params: SazkaEventsDrilldownDetailParams,
        options?: RequestOptions,
    ): Promise<EventDrilldownDetail[]> => {
        const response = await orchestrationsApi.getSazkaEventsDrilldownDetail(params);
        const retVal = await storeEvents(
            apiStore,
            options?.wsData?.stateNameOverride || StateEnum.EventDrilldownDetail,
            response?.data?.events,
            options?.wsData?.update,
            options?.wsData?.removeOthers,
        );
        return retVal;
    };

    const getDetailedSearch = async (params: SazkaDetailedSearchParams): Promise<DetailedSearch> => {
        const response = await orchestrationsApi.getSazkaDetailedSearch(params);
        return response?.search;
    };

    const postBuildSlip = async (data: SlipBuildData, params?: SlipBuildParams): Promise<SlipBuild> => {
        // Basically the same as postSlipBuild but outcomes in response contains 'detail' object
        const response = await orchestrationsApi.postSazkaBuildSlip(data, params);
        return response;
    };

    // bet-service-api
    const getBets = async (params: BetsParams, options?: RequestOptions): Promise<BetsDataWithPaginationToken> => {
        const response = await betServiceApi.getBets(params);
        const retVal = await storeBets(apiStore, response?.data, options?.wsData?.update);
        return { data: retVal, paginationToken: betServiceApi.extractPaginationToken(response) };
    };

    const getBet = async (params: BetParams): Promise<BetData> => {
        const response = await betServiceApi.getBet(params);
        return response?.data;
    };

    const patchBetCashout = async (params: BetCashoutParams): Promise<BetResponse> => {
        const response = await betServiceApi.patchBetCashout(params);
        return response;
    };

    const postSlipBet = async (data: SlipBetData): Promise<SlipBet> => {
        const response = await betServiceApi.postSlipBet(data);
        return response;
    };

    const postSlipBuild = async (data: SlipBuildData, params?: SlipBuildParams): Promise<SlipBuild> => {
        const response = await betServiceApi.postSlipBuild(data, params);
        return response;
    };

    const getBIGeneralBetratio = async (params: { id: string | number }): Promise<BIGeneralBetratio> => {
        const response = await bigeneralApi.getBIGeneralBetratio(params);
        return response?.[0];
    };

    // inagami
    const getPregenerated = async (params: { matchIds: string[] | number[] }): Promise<GetPregenerated[]> => {
        const response = await inagamiApi.getPregenerated(params);
        return response;
    };

    // sports-service
    const getEventAccessStream = async (params: { eventId: string | number }): Promise<EventAccessStream> => {
        const response = await sportsServiceApi.getEventAccessStream(params);
        return response;
    };
    const getCashoutSubscription = async (): Promise<CashoutSubscription> => {
        const response = await sportsServiceApi.getCashoutSubscription();
        return response;
    };

    // v1-api
    const getCachedPrematchLeagues = async (
        leagueIds: number[] | string[],
        options?: RequestOptions,
    ): Promise<PrematchLeaguesResponse> => {
        const response = await v1Api.getPrematchLeagues(leagueIds);
        return {
            ...response,
            data: await storeEvents(
                apiStore,
                StateEnum.EventDrilldown,
                response?.data,
                options?.wsData?.update,
                options?.wsData?.removeOthers,
            ),
        };
    };
    const getCachedTrendingEventsWithBetratio = async (options?: RequestOptions): Promise<EventDrilldown[]> => {
        const response = await v1Api.getTrendingEventsWithBetratio();
        return await storeEvents(
            apiStore,
            StateEnum.EventDrilldownWithBetratio,
            response,
            options?.wsData?.update,
            options?.wsData?.removeOthers,
        );
    };

    return {
        getDrilldownNodes,
        getEvents,
        getEventsDetail,
        getTrendingEvents,
        getSearch,
        getSports,
        getCmsContent,
        getSazkaLeagues,
        getOutrightsEvents,
        getEventsDrilldown,
        getTrendingEventsDrilldown,
        getEventsDrilldownDetail,
        getDetailedSearch,
        postSlipBet,
        postBuildSlip,
        getBets,
        getBet,
        patchBetCashout,
        postSlipBuild,
        getBIGeneralBetratio,
        getPregenerated,
        getEventAccessStream,
        getCashoutSubscription,
        getCachedPrematchLeagues,
        getCachedTrendingEventsWithBetratio,
    };
}
